import { Link } from "react-router-dom";
import { MessageCircle, Mail } from "lucide-react";
import { Logo } from "./Header";
import { waLink, track } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const COLS = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "/#platform" },
      { label: "Pricing", href: "/pricing" },
      { label: "Product Demo", href: "/#demo" },
      { label: "Security", href: "/#security" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Training Organizations", href: "/solutions/training-organizations" },
      { label: "NGOs", href: "/solutions/ngos" },
      { label: "Small Businesses", href: "/solutions/small-businesses" },
      { label: "Educational Institutes", href: "/solutions/educational-institutes" },
      { label: "Multi-Center Organizations", href: "/solutions/multi-center-organizations" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "FAQ", href: "/faq" },
      { label: "Finance Management Software", href: "/finance-management-software" },
      { label: "Payroll Software", href: "/payroll-software" },
      { label: "HR & Payroll Software", href: "/hr-payroll-software" },
      { label: "Expense Management", href: "/expense-management-software" },
      { label: "Attendance Software", href: "/attendance-management-software" },
      { label: "Business Management Software", href: "/business-management-software" },
      { label: "Multi Centre Management", href: "/multi-branch-management-software" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Book a Demo", href: "/demo" },
      { label: "Privacy Policy", href: "/legal/privacy" },
      { label: "Terms of Service", href: "/legal/terms" },
      { label: "Refund & Cancellation", href: "/legal/refund" },
      { label: "Sitemap", href: "/sitemap.xml", external: true },
    ],
  },
];

export default function Footer() {
  const { settings } = useSite();
  const wa = settings.whatsapp_number || "919162055572";
  const waDisplay = `+91 ${wa.replace(/^91/, "").replace(/(\d{5})(\d{5})/, "$1 $2")}`;
  const social = Object.entries(settings.social || {}).filter(([, v]) => v);
  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-lg font-display">M</span>
              <span className="font-display font-bold text-lg text-white">Mashara <span className="text-emerald-400">Finance</span></span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6" data-testid="footer-tagline">
              {settings.footer_tagline || "One powerful platform to manage Finance, HR, Attendance, Payroll, Expenses, Invoices and Business Operations."}
            </p>
            <div className="space-y-2">
              <a href={waLink("Hello, I want to know more about Mashara Finance and its pricing.")}
                target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
                onClick={() => track("whatsapp_click", { location: "footer" })}
                className="flex items-center gap-2 text-sm hover:text-emerald-400 transition-colors">
                <MessageCircle className="w-4 h-4" /> {waDisplay}
              </a>
              <a href={`mailto:${settings.contact_email || "kumarshailendra259@gmail.com"}`} data-testid="footer-email-link"
                className="flex items-center gap-2 text-sm hover:text-emerald-400 transition-colors">
                <Mail className="w-4 h-4" /> Contact Sales
              </a>
              {social.length > 0 && (
                <div className="flex gap-3 pt-2" data-testid="footer-social">
                  {social.map(([k, v]) => (
                    <a key={k} href={v} target="_blank" rel="noopener noreferrer" aria-label={k} className="text-xs capitalize text-slate-400 hover:text-emerald-400">{k}</a>
                  ))}
                </div>
              )}
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="text-white font-semibold text-sm mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.external ? (
                      <a href={l.href} className="text-sm text-slate-400 hover:text-emerald-400 transition-colors">{l.label}</a>
                    ) : (
                      <Link to={l.href} className="text-sm text-slate-400 hover:text-emerald-400 transition-colors">{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Mashara Finance. All rights reserved.</p>
          <div className="flex gap-6 text-xs text-slate-500">
            <Link to="/legal/privacy" className="hover:text-emerald-400">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-emerald-400">Terms</Link>
            <Link to="/legal/refund" className="hover:text-emerald-400">Refunds</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
